// src/sim/bot/brain.ts — Bot beyni (DESIGN §12, ARCHITECTURE §8): 10 Hz karar (`think`, id kaydırmalı), reactionTime gecikmeli tehdit
// tepkisi, öncelik zinciri 0–7 (`if/return` blokları), kenar vetosu, ganging sınırı (dünya düzeyi sayaç), InputFrame üretimi (`frame`).
// Bot oyuncuyla AYNI InputFrame yolunu kullanır (hile yok): stick + tuş bitleri; `pressed/released` yalnız değişim tick'inde.
// Tüm rastgelelik hashNoise(mixedSeed(bot seed, world.seed), tick, tuz) — Math.random / Date.now yok. Sıcak yolda tahsis yok (scratch alanlar sınıfta).
// three import ETMEZ; başka paketin dosyasını import etmez (yalnız core/config + perception.ts).
import { Btn, FState, MovePhase } from '../../core/types.ts';
import type { Bot, BotParams, Difficulty, Fighter, InputFrame, SimWorld, Vec3 } from '../../core/types.ts';
import { inputFrame } from '../../core/types.ts';
import { hashNoise, hashNoiseSigned, clamp01, degToRad } from '../../core/math.ts';
import { v3 } from '../../core/vec3.ts';
import { BTN_BITS, MAX_FIGHTERS, NO_ID, NO_TICK, TICK_DT, TICK_HZ } from '../../config/constants.ts';
import { T } from '../../config/tuning.ts';
import { btnIndex } from '../../config/controls.ts';
import { createPerception, perceive, isEnemyAlive } from './perception.ts';
import type { BotPerception } from './perception.ts';

// ── WP5 yerel sabitler (tuning'de karşılığı yok → rapor needsWP0) ──
/** Yakın dövüş menzili (merkezler arası, u): jab reach 0.98 + hurtbox 0.45 ≈ 1.4; silah `ITEMS.reachBonus` eklenir. */
const MELEE_RANGE = 1.5;
/** Dash Tackle'ın mantıklı olduğu üst mesafe (1.2 u kök hareket + koşu momentumu + reach). */
const DASH_RANGE = 3.5;
/** Fırlatma için kenara yaklaşma payı (u): bu mesafede Carry biter, B ile dışa fırlatılır. */
const THROW_EDGE_DIST = 1.6;
/** Yatan hedefe yürüme mesafesi: bundan uzaktaki yatana kaldırma niyeti kurulmaz (kalkar). */
const LYING_APPROACH_RANGE = 3.0;
/** Strafe yönü kaç tick'te bir yeniden çekilir (0.5 s). */
const STRAFE_HOLD_TICKS = 30;
/** Menzilde saldırırken çubuk büyüklüğü (dönmek için > walkDead, koşma bağlamı açmasın diye < runStickMin). */
const FACE_STICK = 0.35;
/** Rakibe dönmek için çubuk: fren eşiği 0.60'ın ve edgeClip 0.55'in altında (kenarda dönerken düşme yok). */
const TURN_STICK = 0.5;
/** Kenar farkındalığı zarını kaybeden bot için kısa öngörü (thinkTicks + 2 t: bir sonraki karara kadar geçen yol yine kapsanır). */
const SHORT_LOOKAHEAD = T.BOT.thinkTicks + 2;
/** Bir dokunuşun (X/Y/B/A/R1) basılı kalma süresi (t): sim `pressed` kenarını okur; 2 t güvenli. */
const TAP_HOLD = 2;
/** Y'yi ıska takibi için basılı tutma (t): heavyHoldTicks 15 + pay (Kick ıskalarsa Sweep/Leap). */
const HEAVY_HOLD = T.INPUT.heavyHoldTicks + 3;
/** Flying Kick isabet tahmini için ideal yatay mesafe ve tolerans (u). */
const FLYING_KICK_IDEAL = 1.2;
const FLYING_KICK_TOL = 1.5;
// ── Hedef ısı dağılımı (tur-1 bulgusu: normal botlar açılışta insana yığılıyor, sonra AFK oyuncu görmezden geliniyordu) ──
/** Aynı hedefe kesintisiz bu kadar tick (8 s) saldırdıktan sonra hedefin puanı düşer → bot başka rakibe döner. */
const TARGET_FATIGUE_TICKS = 480;
/** Yorgunlukla bırakılan hedefin cezası bu kadar (5 s) sürer: 1 s histeresis biter bitmez geri dönülmesin. */
const TARGET_COOLDOWN_TICKS = 300;
/** Yorgun / soğuyan hedefin puan çarpanı. */
const TARGET_FATIGUE_MULT = 0.6;
/** Başkasının son 1 s içinde vurduğu rakip: yığılma cezası (2v1 freniyle aynı yönde; intikam çarpanı yine geçerli). */
const HEAT_WINDOW_TICKS = 60;
const HEAT_MULT = 0.75;
/** Hareketsiz rakip (Idle ≥ 3 s): hafif ilgi bonusu — AFK oyuncu görmezden gelinmez (insana özel bayrak yok: her duran rakip). */
const AFK_IDLE_TICKS = 180;
const SCORE_AFK = 0.3;
/** Puan eşitliğini (spawn köşeleri: iki rakip 9 u → 1/9 eşit) id sırası yerine deterministik zar kırar; aksi hâlde en düşük id (insan)
 *  her botun ilk hedefi olurdu. İntikam farkı (0.5 vs 0.54) bozulmasın diye küçük. */
const SCORE_JITTER = 0.02;
/** Açılış hoşgörüsü: easy/normal bot maçın ilk 10 s'inde yatanı kaldırıp atmaz (Kick serbest); hard'da yok. DESIGN §12'de yok → deviation. */
const OPENING_GRACE_TICKS = 600;
/** Kaldırma zarı anahtarı "yok" değeri: lyingUntil NO_TICK de olabildiğinden (kurulum/stub) ayrı nöbetçi. */
const NO_KEY = Number.MIN_SAFE_INTEGER;
/** Saldırı konisi YARI açısı: sim `aimTarget` artık `cosHalf = cos(halfConeDeg)` (moves.ts, düzeltici tur 2) ve T.AIM.*ConeDeg
 *  değerleri "±" anlamlıdır (DESIGN §11 koni ±60°) → bot da ±60° kullanır. `* 0.5` bot'u ±30°'a hapsediyordu: menzildeki rakibe
 *  basmak yerine önce dönüyordu (DESIGN §12 "aynı menzil/koni" kuralı). */
const ATTACK_HALF_CONE = degToRad(T.AIM.attackConeDeg);
/** Fırsat (d): tumble'da kenara uçan hedefin iniş noktası için öngörü (s) ve kenar tarafında durma payı (× melee menzili). */
const TUMBLE_PREDICT_SEC = 0.4;
const EDGE_STAND_FRAC = 0.8;
/** Tur-2 his bulgusu: normal bot zinciri (L1→L2→L3→Kick) bitince hemen yenisine başlıyor, oyuncuya tepki penceresi kalmıyordu.
 *  Zincir bittikten (hamle durumu kapandıktan) sonra bu kadar tick yeni seri açılmaz (yüz dönük bekler; tehdit/fırsat dalları serbest).
 *  Zorluk tablosunda alan yok → yerel (needsWP0); easy değişmez (DESIGN "Kolay ayarı değişmesin"), hard'da pencere yok. */
const CHAIN_REST_TICKS: Readonly<Record<Difficulty, number>> = { easy: 0, normal: 30, hard: 0 };
/** Tur-3 his bulgusu: "DÖVÜŞ!"ten 1.2 s sonra ilk Dash Tackle isabet ediyor, oyuncu daha yönünü bulmadan ilk canını kaybediyordu.
 *  Açılışta saldırganlık 0'dan tam değere bu kadar tick'te rampalanır (saldırı zarları + Dash Tackle; konumlanma/tehdit dalları
 *  etkilenmez — bot yaklaşır ve savunur, yalnız hemen basmaz). Rampa YALNIZ geri sayımdan çıkan maçın açılışında işler; hard'da
 *  yok (DESIGN §12 "hard 1v1'de normal'i > %65 yener" kabulü zayıflamasın). Zorluk tablosunda alan yok → yerel (needsWP0). */
const OPENING_RAMP_TICKS: Readonly<Record<Difficulty, number>> = { easy: 240, normal: 180, hard: 0 };

/** hashNoise tuzları (aynı tick'te farklı kararlar bağımsız). SALT_TARGET + rakip id (20..27) hedef eşitlik kırıcı için ayrılmıştır. */
const SALT_EVASION = 1, SALT_PARRY = 2, SALT_SIDE = 3, SALT_STRAFE = 4, SALT_DASH = 5, SALT_FINISH = 6, SALT_FINISH_KIND = 7,
  SALT_CARRY = 8, SALT_TAUNT = 9, SALT_DI = 10, SALT_EDGE = 11, SALT_AIM = 12, SALT_WAKE = 13, SALT_GRAB_JUMP = 14,
  SALT_AGGRO = 16, SALT_GAP = 17, SALT_APPROACH = 18, SALT_TARGET = 20;

/** Gecikmeli tepki türleri (pendingReaction). */
const REACT_NONE = 0, REACT_BLOCK = 1, REACT_DODGE = 2, REACT_JUMP = 3, REACT_ATTACK = 4;

/** Öncelik zinciri dalı (test/debug: son think hangi dalda bitti). */
export const BRANCH_IDLE = -1, BRANCH_LYING = 0, BRANCH_SURVIVE = 1, BRANCH_THREAT = 2, BRANCH_OPPORTUNITY = 3,
  BRANCH_ATTACK = 5, BRANCH_POSITION = 6, BRANCH_TAUNT = 7, BRANCH_MASH = 8, BRANCH_CARRY = 9, BRANCH_WAIT = 10;

/** Mash döngüsü: 4 yüz tuşu sırayla (FACE_BUTTONS maskesiyle aynı küme). */
const MASH_CYCLE: readonly Btn[] = [Btn.Jump, Btn.Light, Btn.Heavy, Btn.Grab];

// ── Ganging sayacı: dünya düzeyinde hedef başına bot sayısı (ARCHITECTURE §8 "dünya seviyesinde sayaç") ──
// SimWorld'de alan yok (types.ts WP0) → WeakMap ile dünyaya bağlanır; ilk erişimde bir kez tahsis (kurulum), sonra tahsissiz.
const targetCounts = new WeakMap<SimWorld, Int32Array>();
export function targetCountsOf(world: SimWorld): Int32Array {
  let c = targetCounts.get(world);
  if (!c) { c = new Int32Array(MAX_FIGHTERS); targetCounts.set(world, c); }
  return c;
}

/** Zorluk tablosu kısayolu (T.BOT.params; DESIGN §12). */
export const botParamsOf = (d: Difficulty): BotParams => T.BOT.params[d];

export class BotBrain implements Bot {
  readonly fighterId: number;
  readonly difficulty: Difficulty;
  params: BotParams;
  nextThinkTick: number;
  seed: number;
  readonly intent: InputFrame = inputFrame();
  readonly perception: BotPerception = createPerception();
  targetSince = NO_TICK;
  whiffWaitUntil = NO_TICK;
  reactAt = NO_TICK;
  parryTapAt = NO_TICK;
  /** Son think'in bittiği dal (test/debug). */
  lastBranch = BRANCH_IDLE;
  /** Seçili hedef (perception.targetId geçersizse en yakına düşer; sayaç bu alanla güncellenir). */
  targetId = NO_ID;

  // ── frame() durumu ──
  private prevButtons = 0;
  private tapPending = 0;                        // bu tick basılacak bitler
  private readonly holdLeft = new Int32Array(BTN_BITS);   // bit başına kalan basılı tick
  private readonly tapHold = new Int32Array(BTN_BITS);    // bit başına istenen basılı süre
  private mashing = false;
  private mashAcc = 0;
  private mashIdx = 0;
  // ── tehdit tepkisi ──
  private pendingReaction = REACT_NONE;
  private reactDirX = 0; private reactDirZ = 0;
  private blockUntil = NO_TICK;
  private blockMinUntil = NO_TICK;               // L1 en az bu tick'e kadar basılı (≤ 6 t bırakma = parry dokunuşu olurdu)
  private threatKey = NO_TICK;
  // ── fırsat / saldırı / konumlanma hafızası ──
  private carryKey = NO_KEY; private carryYes = false;   // carryKey = yatanın lyingUntil'i (knockdown başına tek zar)
  /** Kendi yatışım: anahtar = f.lyingUntil (knockdown başına tek; Roll/Crawl geçişlerinde değişmez), başlangıç = knockdown tick'i. */
  private lyingKeyOwn = NO_KEY; private lyingSinceOwn = NO_TICK;
  /** Açılış rampası: geri sayım görüldüyse 'fight'e geçiş tick'i; bu tick'ten OPENING_RAMP_TICKS boyunca saldırganlık 0 → tam. */
  private sawCountdown = false; private fightSince = NO_TICK; private aggroScale = 1;
  /** Yorgunlukla bırakılan hedef ve ceza bitişi (hedef ısı dağılımı). */
  private coolId = NO_ID; private coolUntil = NO_TICK;
  private strafeSide = 1; private strafeUntil = NO_TICK;
  private comboPresses = 0; private lastAttackTick = NO_TICK;
  /** Zincir tamamlandı, hamle bitince dinlenme kurulacak; dinlenme bitiş tick'i. */
  private chainRestPending = false; private chainRestUntil = NO_TICK;
  private sawWhiff = false;
  private worldSeed = 0;
  /** Hedef seçimi scratch'i (tahsissiz). */
  private readonly tmpN: Vec3 = v3();

  constructor(fighterId: number, difficulty: Difficulty, seed: number) {
    this.fighterId = fighterId;
    this.difficulty = difficulty;
    this.params = botParamsOf(difficulty);
    this.seed = seed;
    // id'ye göre kaydırmalı karar: 8 bot 6 t'ye yayılır (aynı tick'te tüm botlar düşünmez)
    this.nextThinkTick = fighterId % T.BOT.thinkTicks;
  }

  // ───────────────────────── yardımcılar ─────────────────────────
  /** Birleşik tohum: bot tohumu × büyük asal + dünya tohumu (XOR'da (k+7)^k gibi çakışmalar oluyordu); tuz fighterId ile ayrışır. */
  private mixedSeed(): number { return (Math.imul(this.seed, 0x9e3779b1) + this.worldSeed) | 0; }
  private noise(tick: number, salt: number): number { return hashNoise(this.mixedSeed(), tick, salt * 131 + this.fighterId); }
  /** Saldırganlık zarı eşiği: tablo değeri × açılış rampası (think başında hesaplanır; rampa dışında 1). */
  private get aggro(): number { return this.params.aggression * this.aggroScale; }
  private noiseSigned(tick: number, salt: number): number { return hashNoiseSigned(this.mixedSeed(), tick, salt * 131 + this.fighterId); }

  /** Bir sonraki frame'de `btn` basılır ve `hold` tick basılı kalır (sonra bırakılır). Aynı bit zaten basılıysa frame önce bırakır. */
  private queueTap(btn: Btn, hold: number): void {
    const i = btnIndex(btn);
    this.tapPending |= btn;
    this.tapHold[i] = hold;
  }
  private setStick(x: number, z: number): void {
    const m = Math.hypot(x, z);
    if (m > 1) { x /= m; z /= m; }
    this.intent.moveX = x; this.intent.moveZ = z;
  }
  /** Çubuk yönüne aimError (°) kadar deterministik hata ekler (DESIGN §12 aimError). */
  private applyAimError(tick: number): void {
    const x = this.intent.moveX, z = this.intent.moveZ;
    if (x === 0 && z === 0) return;
    const a = degToRad(this.params.aimErrorDeg) * this.noiseSigned(tick, SALT_AIM);
    const c = Math.cos(a), s = Math.sin(a);
    this.intent.moveX = x * c - z * s;
    this.intent.moveZ = x * s + z * c;
  }

  /**
   * Kenar vetosu (ARCHITECTURE §8): pred = pos + vel·t + intent·runSpeed·t platform dışına çıkacaksa çubuğun dış bileşeni
   * `edgeClip` (0.55, fren eşiği altı → sim kenarda durdurur) ile kırpılır. Bilinçli düşme YOK: kırpma her zaman uygulanır;
   * `edgeAwareness` yalnız öngörü ufkunu belirler (zar kaybedilirse kısa ufuk → geç fark eder ama yine düşmez).
   */
  private applyEdgeVeto(world: SimWorld, f: Fighter, tick: number): void {
    const mx = this.intent.moveX, mz = this.intent.moveZ;
    if (mx === 0 && mz === 0) return;
    if (!f.grounded) return;   // havada fren yok (toparlanma dalı zaten merkeze yönlendirir)
    const aware = this.noise(tick, SALT_EDGE) < this.params.edgeAwareness;
    const sec = (aware ? T.BOT.lookaheadTicks : SHORT_LOOKAHEAD) * TICK_DT;
    const secFull = aware ? T.BOT.predictSec : sec;
    const px = f.pos.x + f.vel.x * secFull + mx * T.MOVE.runSpeed * sec;
    const pz = f.pos.z + f.vel.z * secFull + mz * T.MOVE.runSpeed * sec;
    if (world.query.isInside(px, pz)) return;
    // öngörülen nokta dışarıda: o noktaya göre dış normal (köşede doğru kenarı seçer)
    world.query.edgeDist(px, pz, this.tmpN);
    const nx = this.tmpN.x, nz = this.tmpN.z;
    const out = mx * nx + mz * nz;
    if (out <= T.BOT.edgeClip) return;
    const k = out - T.BOT.edgeClip;
    this.intent.moveX = mx - nx * k;
    this.intent.moveZ = mz - nz * k;
  }

  /**
   * Hedef seçimi (DESIGN §12 4): puan tablosu + 1 s histeresis + maxBotsPerTarget (dünya sayacı). Ek (tur-1): ısı dağılımı —
   * aynı hedefe uzun süre saldırma yorgunluğu + soğuma, başkasının az önce vurduğu rakibe yığılma cezası, duran (AFK) rakibe hafif
   * ilgi, eşitlik kırıcı zar (id sırası = insan önyargısı olmasın; DESIGN "oyuncuya önyargı yok").
   */
  private selectTarget(world: SimWorld, f: Fighter, tick: number): void {
    const counts = targetCountsOf(world);
    const cur = this.targetId !== NO_ID ? world.fighters[this.targetId] : undefined;
    const curValid = !!cur && cur.id !== f.id && isEnemyAlive(cur);
    if (curValid && tick - this.targetSince < T.BOT.targetHysteresisTicks) return;
    const maxPer = T.BOT.maxBotsPerTarget[this.difficulty];
    const curTired = curValid && tick - this.targetSince >= TARGET_FATIGUE_TICKS;
    let best = NO_ID, bestScore = -Infinity;          // sınıra uyan en iyi
    let bestAny = NO_ID, bestAnyScore = -Infinity;    // sınır gözetmeksizin (yalnız hepsi doluysa)
    const fighters = world.fighters;
    for (let i = 0; i < fighters.length; i++) {
      const e = fighters[i];
      if (!e || e.id === f.id || !isEnemyAlive(e)) continue;
      const d = Math.hypot(e.pos.x - f.pos.x, e.pos.z - f.pos.z);
      const edge = world.query.edgeDist(e.pos.x, e.pos.z, this.tmpN);
      let s = 1 / Math.max(d, 0.5)
        + T.BOT.scoreDmg * clamp01(e.damage / 100)
        + (e.weapon !== 'unarmed' ? T.BOT.scoreArmed : 0)
        + (edge < T.EDGE.alarmDist ? T.BOT.scoreEdge : 0)
        + SCORE_JITTER * hashNoise(this.mixedSeed(), tick, (SALT_TARGET + e.id) * 131 + this.fighterId);
      // duran rakip (Idle'a girişten ≥ 3 s): hafif ilgi
      if (e.state === FState.Idle && tick - e.stateTick >= AFK_IDLE_TICKS) s += SCORE_AFK;
      if (e.id === f.lastHitBy) s *= T.BOT.scoreRevenge;
      // ısı: yorgun (şu anki, ≥ 8 s) ya da soğuyan (az önce bırakılan) hedef → ×0.6
      if ((curTired && e.id === this.targetId) || (e.id === this.coolId && tick < this.coolUntil)) s *= TARGET_FATIGUE_MULT;
      // yığılma: son 1 s'de BAŞKASI vurmuş → ×0.75 (kendi vuruşum ceza değil: seri sürer)
      if (e.lastHitBy !== NO_ID && e.lastHitBy !== f.id && tick - e.lastHitTick < HEAT_WINDOW_TICKS && tick >= e.lastHitTick) s *= HEAT_MULT;
      if (s > bestAnyScore) { bestAnyScore = s; bestAny = e.id; }
      const others = (counts[e.id] ?? 0) - (e.id === this.targetId ? 1 : 0);   // benim katkım hariç
      if (others >= maxPer) continue;
      if (s > bestScore) { bestScore = s; best = e.id; }
    }
    const pick = best !== NO_ID ? best : bestAny;
    if (pick === this.targetId) { if (!curValid) this.targetSince = tick; return; }
    // yorgun hedef bırakılıyor → soğuma (histeresis bitince hemen geri dönmesin)
    if (curTired) { this.coolId = this.targetId; this.coolUntil = tick + TARGET_COOLDOWN_TICKS; }
    if (this.targetId !== NO_ID && this.targetId < counts.length) counts[this.targetId] = Math.max(0, (counts[this.targetId] ?? 0) - 1);
    if (pick !== NO_ID && pick < counts.length) counts[pick] = (counts[pick] ?? 0) + 1;
    this.targetId = pick;
    this.targetSince = tick;
  }

  /** Bu botun sayaç katkısını bırakır (eleme / dünya değişimi; WP9 isterse çağırır). */
  releaseTarget(world: SimWorld): void {
    const counts = targetCountsOf(world);
    if (this.targetId !== NO_ID && this.targetId < counts.length) counts[this.targetId] = Math.max(0, (counts[this.targetId] ?? 0) - 1);
    this.targetId = NO_ID; this.targetSince = NO_TICK;
  }

  /** Koşma bağlamı (DESIGN §2: hız ≥ 5.5 ∧ çubuk ≥ 0.85 ≥ 10 t) — oyuncuyla aynı kural; sayaç fighter.ts'den (WP2). */
  private runContext(f: Fighter, tick: number): boolean {
    if (f.runContextSince === NO_TICK || f.runContextSince > tick) return false;
    if (tick - f.runContextSince < T.MOVE.runContextTicks) return false;
    return Math.hypot(f.vel.x, f.vel.z) >= T.MOVE.runThreshold;
  }
  private meleeRange(f: Fighter): number { return MELEE_RANGE + T.ITEMS.reachBonus[f.weapon]; }

  // ───────────────────────── think ─────────────────────────
  think(world: SimWorld): void {
    const tick = world.tick;
    this.nextThinkTick = tick + T.BOT.thinkTicks;
    this.worldSeed = world.seed;
    const f = world.fighters[this.fighterId];
    if (!f) return;
    const p = this.perception;
    const P = this.params;

    if (f.state === FState.Eliminated) { if (this.targetId !== NO_ID) this.releaseTarget(world); this.lastBranch = BRANCH_WAIT; return; }
    this.selectTarget(world, f, tick);
    // Hold/Carry'de algı hedefi = tuttuğum kurban (fırlatma yönü onun kenar normalinden); aksi hâlde seçili hedef
    const holding = f.state === FState.Hold || f.state === FState.Carrying || f.state === FState.Lifting;
    perceive(world, f, holding && f.grabTarget !== NO_ID ? f.grabTarget : this.targetId, p);

    // niyet sıfırla (dokunuş kuyruğu ve gecikmeli tepki korunur)
    this.intent.moveX = 0; this.intent.moveZ = 0; this.intent.buttons = 0;
    this.mashing = false;
    this.lastBranch = BRANCH_IDLE;

    // whiff izleme: ilk görüldüğünde 24 t bekleme kur
    if (f.whiffed) { if (!this.sawWhiff) { this.sawWhiff = true; this.whiffWaitUntil = tick + T.BOT.whiffWaitTicks; } }
    else this.sawWhiff = false;
    // kombo sayacı: hamle dışında 24 t geçince sıfır
    if (f.state !== FState.Attack && tick - this.lastAttackTick > T.BOT.whiffWaitTicks) this.comboPresses = 0;
    // zincir dinlenmesi: bitirici hamle kapanınca (Attack dışı) pencere başlar — süre hamlenin kendi recover'ına eklenir
    if (this.chainRestPending && f.state !== FState.Attack) { this.chainRestPending = false; this.chainRestUntil = tick + CHAIN_REST_TICKS[this.difficulty]; }

    // Açılış rampası: geri sayımı gördüysek 'fight'in ilk tick'inden itibaren saldırganlık 0 → tam (hard'da ramp 0 → hep 1).
    // Geri sayım hiç görülmediyse (stub / maç ortasında kurulan bot) rampa yok: tam saldırganlık.
    if (world.match.phase === 'countdown') { this.sawCountdown = true; this.fightSince = NO_TICK; }
    else if (world.match.phase === 'fight' && this.sawCountdown && this.fightSince === NO_TICK) this.fightSince = tick;
    const ramp = OPENING_RAMP_TICKS[this.difficulty];
    this.aggroScale = ramp > 0 && this.fightSince !== NO_TICK ? clamp01((tick - this.fightSince) / ramp) : 1;

    // Yatış başlangıcı: `f.lyingUntil` knockdown başına TEK anahtardır (enterLying'de yazılır; Lying↔Crawl ve Roll geçişleri
    // değiştirmez) → süreyi durum girişinden (stateTick) değil knockdown başından ölçeriz. stateTick ile ölçmek, Roll ya da
    // sürünme sonrası sayacı sıfırlıyor ve bot getUpDelayTicks'i baştan bekliyordu; sim'in kendi ≥ 30 t kuralı (knockdown.ts
    // lyingSince) knockdown başından sayar → A basışı reddedilmiyor, yalnız gecikiyordu (DESIGN §12 0.(b), "Lying < 70 t").
    const lyingNow = f.state === FState.Lying || f.state === FState.Crawl || f.state === FState.Roll;
    if (lyingNow && f.lyingUntil !== NO_TICK) {
      // ilk gözlemde stateTick = enterLying tick'i (Roll ancak bir think sonrası başlayabilir) → knockdown başı
      if (this.lyingKeyOwn !== f.lyingUntil) { this.lyingKeyOwn = f.lyingUntil; this.lyingSinceOwn = f.stateTick; }
    } else if (!lyingNow) { this.lyingKeyOwn = NO_KEY; this.lyingSinceOwn = NO_TICK; }

    // ── durum kapıları: kontrolsüz / özel durumlar ──
    switch (f.state) {
      case FState.Held: case FState.Carried: case FState.Escaping:
        this.mashing = true; this.lastBranch = BRANCH_MASH; return;                  // kurtulma mash'i (DESIGN §6)
      case FState.Hold:
        this.thinkHold(f, tick); return;
      case FState.Carrying:
        this.thinkCarry(world, f, tick); return;
      case FState.Lying:
        this.thinkLying(f, tick); return;                                             // 0. yerdeyken
      case FState.Crawl:
        this.thinkLying(f, tick); return;
      case FState.Hitstun: case FState.Launched: case FState.Tumble:
        this.thinkDI(f, tick); return;                                                // 1. hayatta kal (DI / kontrol dönüşü)
      case FState.Knockdown: case FState.Roll: case FState.GetUp: case FState.Lifting: case FState.PickingUp:
      case FState.Throwing: case FState.GrabAttempt: case FState.Dodge: case FState.AirDodge:
      case FState.Spawning: case FState.Respawning: case FState.Falling:   // Eliminated yukarıda (sayaç bırakılır)
        this.lastBranch = BRANCH_WAIT; return;
      default: break;
    }

    // ── 1. hayatta kal: havada ve platform dışında ──
    if (!f.grounded && p.outside) { this.thinkRecovery(f, tick); return; }

    // ── 2. tehdit tepkisi ──
    if (this.thinkThreat(f, tick)) { this.finishMove(world, f, tick); return; }

    // ── 3. fırsat ──
    if (this.thinkOpportunity(world, f, tick)) { this.finishMove(world, f, tick); return; }

    // ── 5. saldırı ──
    if (this.thinkAttack(f, tick)) { this.finishMove(world, f, tick); return; }

    // ── 6. konumlanma ──
    this.thinkPosition(f, tick);

    // ── 7. taunt: rakip ring-out olunca, kenara > 3 u ──
    if (p.fellId !== NO_ID && (f.state === FState.Idle || f.state === FState.Move) && p.myEdgeDist > T.BOT.tauntEdgeMin &&
        p.threatId === NO_ID) {
      const chance = this.difficulty === 'easy' ? T.BOT.tauntChanceEasy : T.BOT.tauntChance;
      if (this.noise(tick, SALT_TAUNT) < chance) { this.setStick(0, 0); this.queueTap(Btn.Taunt, TAP_HOLD); this.lastBranch = BRANCH_TAUNT; }
    }
    this.finishMove(world, f, tick);
  }

  /** Çubuk son işlem: nişan hatası + kenar vetosu. */
  private finishMove(world: SimWorld, f: Fighter, tick: number): void {
    this.applyAimError(tick);
    this.applyEdgeVeto(world, f, tick);
  }

  // ── 0. yerdeyken (Lying / LyingFront / Crawl) ──
  private thinkLying(f: Fighter, tick: number): void {
    const p = this.perception;
    const P = this.params;
    this.lastBranch = BRANCH_LYING;
    const near = p.nearestId !== NO_ID;
    // Yatma süresi KNOCKDOWN başından (think()'te izlenen lyingSinceOwn); anahtar yoksa (stub/kurulum: lyingUntil = NO_TICK)
    // durum girişine düşer. (Tur-1 bulgusu: `stateTick >= …` mutlak tick'i süreyle karşılaştırıyordu; tur-3: stateTick Roll/Crawl
    // geçişinde sıfırlanıyordu → yuvarlanan bot getUpDelayTicks'i baştan bekliyordu.)
    const lyingSince = this.lyingSinceOwn !== NO_TICK ? this.lyingSinceOwn : f.stateTick;
    const lyingTicks = tick - lyingSince;
    // (c) rakip ≤ 1.5 u ve wakeKickChance → kalkış tekmesi (Roll yerine; zar KNOCKDOWN başına bir kez: yatış başlangıcı anahtar)
    if (near && p.nearestDist <= T.BOT.wakeKickRange && !f.getUpKick && this.noise(lyingSince, SALT_WAKE) < P.wakeKickChance) {
      this.setStick(p.nearestDirX * FACE_STICK, p.nearestDirZ * FACE_STICK);
      this.queueTap(Btn.Heavy, TAP_HOLD); return;
    }
    // (a) rakip ≤ 2.5 u ve yaklaşıyorsa Roll rakipten uzağa; yön kenara bakıyorsa dik yön; 1×
    if (near && p.nearestDist <= T.BOT.lyingRollRange && p.nearestApproaching && !f.rollUsed && f.state === FState.Lying) {
      let rx = -p.nearestDirX, rz = -p.nearestDirZ;
      const outward = rx * p.myEdgeNx + rz * p.myEdgeNz;
      if (outward > 0 && p.myEdgeDist < T.KNOCKDOWN.rollDist + T.EDGE.alarmDist) {
        // kenara bakıyor → dik yön (iki dikten merkeze yakın olanı)
        const ax = -rz, az = rx;
        const s = ax * p.centerDirX + az * p.centerDirZ >= 0 ? 1 : -1;
        rx = ax * s; rz = az * s;
      }
      this.setStick(rx, rz);
      this.queueTap(Btn.Dodge, TAP_HOLD); return;
    }
    // (b) Lying ≥ getUpDelayTicks → A hızlı kalk (sim eşiği quickGetUpAfter ile de uyumlu olsun)
    if (f.state === FState.Lying && lyingTicks >= Math.max(P.getUpDelayTicks, T.KNOCKDOWN.quickGetUpAfter)) {
      this.setStick(0, 0);
      this.queueTap(Btn.Jump, TAP_HOLD); return;
    }
    // (d) eşya ≤ 1.0 u ve rakip ≥ 3 u → sürün + B
    if (p.itemId !== NO_ID && p.itemDist <= T.BOT.itemCrawlRange && (!near || p.nearestDist >= T.BOT.itemCrawlEnemyMin)) {
      this.setStick(p.itemDirX, p.itemDirZ);
      this.queueTap(Btn.Grab, TAP_HOLD); return;
    }
    // aksi hâlde bekle (ya da sürünüyorsa rakipten uzaklaş)
    if (f.state === FState.Crawl && near) this.setStick(-p.nearestDirX, -p.nearestDirZ);
    else this.setStick(0, 0);
  }

  // ── 1. hayatta kal: DI merkeze (diAccuracy), tumble kontrol dönüşünde toparlanma ──
  private thinkDI(f: Fighter, tick: number): void {
    const p = this.perception;
    this.lastBranch = BRANCH_SURVIVE;
    if (f.tumbleControl && p.outside && !f.grounded) { this.thinkRecovery(f, tick); return; }
    if (this.noise(tick, SALT_DI) < this.params.diAccuracy) this.setStick(p.centerDirX, p.centerDirZ);
    else this.setStick(0, 0);
  }

  /** Platform dışında havada: hedef en yakın kenar + recoveryMargin (gapAccuracy hata payı); çift zıplama → hava dodge. */
  private thinkRecovery(f: Fighter, tick: number): void {
    const p = this.perception;
    this.lastBranch = BRANCH_SURVIVE;
    // dış normal dışarıyı gösterir → içeri −n; kenar + 1.5 u hedefi için yön aynı, büyüklük tam
    let dx = -p.myEdgeNx, dz = -p.myEdgeNz;
    if (dx === 0 && dz === 0) { dx = p.centerDirX; dz = p.centerDirZ; }
    // gapAccuracy: hata açısı (1 − acc)·±90°
    const err = (1 - this.params.gapAccuracy) * this.noiseSigned(tick, SALT_GAP) * (Math.PI / 2);
    const c = Math.cos(err), s = Math.sin(err);
    this.setStick(dx * c - dz * s, dx * s + dz * c);
    if (f.doubleJumpLeft > 0) this.queueTap(Btn.Jump, TAP_HOLD);
    else if (f.airDodgeLeft > 0) this.queueTap(Btn.Dodge, TAP_HOLD);
  }

  // ── 2. tehdit tepkisi ──
  /** true → bu think tehditle meşgul (saldırı/konumlanma yok). */
  private thinkThreat(f: Fighter, tick: number): boolean {
    const p = this.perception;
    const P = this.params;
    // devam eden blok tepkisi: tehdit sürerken ve reevalAfterHitTicks dolmadan L1 BASILI kalır (tutulan blok parry olmaz — DESIGN §7).
    // Tehdit erken bitse de en az parryTapTicks + 1 t basılı kalır: ≤ 6 t'de bırakma sim'de PARRY DOKUNUŞU sayılır (refrakter cezası).
    if (this.blockUntil > tick) {
      if (p.threatId === NO_ID && tick >= this.blockMinUntil) { this.blockUntil = NO_TICK; }
      else {
        this.intent.buttons |= Btn.Block;
        this.setStick(0, 0);
        this.lastBranch = BRANCH_THREAT;
        return true;
      }
    }
    if (p.threatId === NO_ID) return this.pendingReaction !== REACT_NONE;
    // aynı tehdide bir kez karar (anahtar: kim + hamle başlangıç tick'i)
    const key = p.threatId * 1_000_000 + (p.threatStartTick - NO_TICK);
    if (key !== this.threatKey) {
      this.threatKey = key;
      // parry dokunuşu: parryChance tutarsa öngörülen impact − 3 t'de L1 basış, sonraki tick bırakış (tutma parry edilmez)
      const tapAt = tick + p.threatTicks - T.BOT.parryTapLead;
      if (!p.threatGrab && P.parryChance > 0 && tapAt >= tick + 1 && this.noise(tick, SALT_PARRY) < P.parryChance) {
        this.parryTapAt = tapAt;
        this.pendingReaction = REACT_NONE;
        this.blockUntil = NO_TICK;
        this.lastBranch = BRANCH_THREAT;
        this.setStick(0, 0);
        return true;
      }
      if (this.noise(tick, SALT_EVASION) < P.evasion) {
        this.reactAt = tick + P.reactionTicks;
        if (p.threatGrab) {
          // tutma windup'ı: zıpla ya da vur
          this.pendingReaction = this.noise(tick, SALT_GRAB_JUMP) < 0.5 ? REACT_JUMP : REACT_ATTACK;
          this.reactDirX = p.threatDirX * FACE_STICK; this.reactDirZ = p.threatDirZ * FACE_STICK;
        } else if (p.threatHeavy) {
          // ağıra dodge: rakibe dik yön (yan seçimi zarla; kenara bakan yan yerine merkeze yakın olan)
          const side = this.noise(tick, SALT_SIDE) < 0.5 ? 1 : -1;
          let dx = -p.threatDirZ * side, dz = p.threatDirX * side;
          if (dx * p.myEdgeNx + dz * p.myEdgeNz > 0 && p.myEdgeDist < T.DODGE.distance + T.EDGE.alarmDist) { dx = -dx; dz = -dz; }
          this.reactDirX = dx; this.reactDirZ = dz;
          this.pendingReaction = REACT_DODGE;
        } else {
          this.pendingReaction = REACT_BLOCK;
        }
      }
    }
    if (this.pendingReaction !== REACT_NONE || this.parryTapAt >= tick) {
      // tepki bekleniyor: yerinde dur (rakibe dön), saldırma
      this.setStick(p.threatDirX * FACE_STICK, p.threatDirZ * FACE_STICK);
      this.lastBranch = BRANCH_THREAT;
      return true;
    }
    return false;
  }

  /** frame(): reactAt geldiğinde gecikmeli tepkiyi niyete uygular. */
  private applyReaction(tick: number): void {
    const kind = this.pendingReaction;
    this.pendingReaction = REACT_NONE;
    switch (kind) {
      case REACT_BLOCK:
        this.intent.buttons |= Btn.Block;
        this.intent.moveX = 0; this.intent.moveZ = 0;
        this.blockUntil = tick + T.BOT.reevalAfterHitTicks;   // vurulmadan 0.4 s → tekrar değerlendir
        this.blockMinUntil = tick + T.BLOCK.parryTapTicks + 1; // 7 t altı bırakma parry dokunuşu sayılır → en az 7 t tut
        break;
      case REACT_DODGE:
        this.intent.moveX = this.reactDirX; this.intent.moveZ = this.reactDirZ;
        this.queueTap(Btn.Dodge, TAP_HOLD);
        break;
      case REACT_JUMP:
        this.queueTap(Btn.Jump, TAP_HOLD);
        break;
      case REACT_ATTACK:
        this.intent.moveX = this.reactDirX; this.intent.moveZ = this.reactDirZ;
        this.queueTap(Btn.Light, TAP_HOLD);
        break;
      default: break;
    }
  }

  // ── 3. fırsat ──
  private thinkOpportunity(world: SimWorld, f: Fighter, tick: number): boolean {
    const p = this.perception;
    const P = this.params;
    const range = this.meleeRange(f);
    // (a) rakip yerde ≤ 1.2 u ve kenara ≤ 7 u → carryToEdge ile kaldır (silahsız); zar KNOCKDOWN başına bir kez (lyingUntil anahtarı —
    // eskiden yatan id'si anahtardı: tek insan rakibe zar maç boyunca bir kez atılıyor, %40 "hep ya da hiç" oluyordu).
    // Ganging sınırı burada da geçerli: hedefim olmayan yatana, ona zaten maxBotsPerTarget bot saldırıyorsa üşüşmem.
    const lyingGanged = p.lyingId !== NO_ID && p.lyingId !== this.targetId &&
      (targetCountsOf(world)[p.lyingId] ?? 0) >= T.BOT.maxBotsPerTarget[this.difficulty];
    if (p.lyingId !== NO_ID && p.lyingDist <= LYING_APPROACH_RANGE && !lyingGanged) {
      if (this.carryKey !== p.lyingKey) { this.carryKey = p.lyingKey; this.carryYes = this.noise(tick, SALT_CARRY) < P.carryToEdge; }
      // açılış hoşgörüsü: easy/normal ilk 10 s'de kaldır-fırlat yok (oyuncu 8. saniyede hamle şansı olmadan ring-out oluyordu)
      const graceOver = this.difficulty === 'hard' || tick >= OPENING_GRACE_TICKS;
      const canCarry = this.carryYes && graceOver && f.weapon === 'unarmed' && p.lyingEdgeDist <= T.BOT.carryEdgeMax;
      if (canCarry) {
        if (p.lyingDist <= T.GRAB.pickUpRange) {
          this.setStick(p.lyingDirX * FACE_STICK, p.lyingDirZ * FACE_STICK);
          if (f.state !== FState.Attack) this.queueTap(Btn.Grab, TAP_HOLD);
        } else this.setStick(p.lyingDirX, p.lyingDirZ);
        this.lastBranch = BRANCH_OPPORTUNITY;
        return true;
      }
      // kaldırmıyorsa: yatana Kick (hitsLying) — aggression'a göre
      if (p.lyingDist <= T.GRAB.pickUpRange && this.noise(tick, SALT_AGGRO) < this.aggro) {
        this.setStick(p.lyingDirX * FACE_STICK, p.lyingDirZ * FACE_STICK);
        if (f.state !== FState.Attack) this.queueTap(Btn.Heavy, TAP_HOLD);
        this.lastBranch = BRANCH_OPPORTUNITY;
        return true;
      }
    } else if (p.lyingId === NO_ID) this.carryKey = NO_KEY;
    // (b) rakip stun ≥ 55 menzilde → Y ailesi (bitir)
    if (p.targetId !== NO_ID && !p.targetLying && p.targetStun >= T.BOT.finishStun && p.targetDist <= range &&
        Math.abs(p.targetDy) <= T.AIM.maxHeightDiff && f.state !== FState.Attack) {
      this.setStick(p.dirX * FACE_STICK, p.dirZ * FACE_STICK);
      this.queueTap(Btn.Heavy, HEAVY_HOLD);
      this.comboPresses = 0; this.lastAttackTick = tick;
      this.lastBranch = BRANCH_OPPORTUNITY;
      return true;
    }
    // (c) yerde silah ≤ 5 u ve rakip ≥ 3 u → al
    if (p.itemId !== NO_ID && f.weapon === 'unarmed' && p.itemDist <= T.BOT.weaponSeekRange &&
        (p.nearestId === NO_ID || p.nearestDist >= T.BOT.weaponSeekEnemyMin)) {
      if (p.itemDist <= T.ITEMS.pickupRange) {
        this.setStick(p.itemDirX * FACE_STICK, p.itemDirZ * FACE_STICK);
        this.queueTap(Btn.Grab, TAP_HOLD);
      } else this.setStick(p.itemDirX, p.itemDirZ);
      this.lastBranch = BRANCH_OPPORTUNITY;
      return true;
    }
    // (d) rakip tumble'da kenara doğru uçuyorsa kenar konumu: iniş noktasının MERKEZ tarafında (−n · menzil·0.8) dur — inince (a)/Kick,
    // toparlanırsa kenarda karşılanır. Menzil içindeyse saldırı dalı (havadaki hedefe Kick) karar verir; kenar vetosu çubuğu kırpar.
    if (p.targetId !== NO_ID && p.targetTumbling && p.targetVelOut > 0 && p.targetDist > range) {
      const tgt = world.fighters[p.targetId];
      const vx = tgt ? tgt.vel.x : 0, vz = tgt ? tgt.vel.z : 0;
      const gx = p.targetX + vx * TUMBLE_PREDICT_SEC - p.targetEdgeNx * range * EDGE_STAND_FRAC;
      const gz = p.targetZ + vz * TUMBLE_PREDICT_SEC - p.targetEdgeNz * range * EDGE_STAND_FRAC;
      let dx = gx - f.pos.x, dz = gz - f.pos.z;
      const gd = Math.hypot(dx, dz);
      if (gd > 1e-6) { dx /= gd; dz /= gd; }
      // hedef noktaya yakınsak yüzü rakibe dön (koşma bağlamı açılmasın), uzaksa tam çubuk
      if (gd <= range * 0.5) this.setStick(p.dirX * FACE_STICK, p.dirZ * FACE_STICK);
      else this.setStick(dx, dz);
      this.lastBranch = BRANCH_OPPORTUNITY;
      return true;
    }
    return false;
  }

  // ── Hold: B → fırlat, çubuk = hedefin kenar dış normali (en yakın kenardan dışarı) ──
  private thinkHold(_f: Fighter, _tick: number): void {
    const p = this.perception;
    this.lastBranch = BRANCH_CARRY;
    if (p.targetEdgeNx !== 0 || p.targetEdgeNz !== 0) this.setStick(p.targetEdgeNx, p.targetEdgeNz);
    else this.setStick(-p.centerDirX, -p.centerDirZ);
    this.queueTap(Btn.Grab, TAP_HOLD);
  }

  // ── Carry: en yakın kenara taşı, ≤ THROW_EDGE_DIST'te dışa fırlat (kenar vetosu 0.55 kırpar → taşıyan düşmez) ──
  private thinkCarry(world: SimWorld, f: Fighter, tick: number): void {
    const p = this.perception;
    this.lastBranch = BRANCH_CARRY;
    let nx = p.myEdgeNx, nz = p.myEdgeNz;
    if (nx === 0 && nz === 0) { nx = -p.centerDirX; nz = -p.centerDirZ; }
    if (p.myEdgeDist <= THROW_EDGE_DIST || !this.carryYes) {
      this.setStick(nx, nz);                  // fırlatma yönü = tam çubuk (dışarı)
      this.queueTap(Btn.Grab, TAP_HOLD);
    } else {
      this.setStick(nx, nz);
    }
    this.finishMove(world, f, tick);
  }

  // ── 5. saldırı ──
  private thinkAttack(f: Fighter, tick: number): boolean {
    const p = this.perception;
    const P = this.params;
    if (p.targetId === NO_ID) return false;
    if (this.whiffWaitUntil > tick) return false;           // whiff sonrası 24 t bekle
    const range = this.meleeRange(f);
    const halfCone = ATTACK_HALF_CONE;
    const running = this.runContext(f, tick);

    // havadayım (tutma kaçışı zıplaması): Flying Kick yalnız isabet olasılığı > 0.6
    if (f.state === FState.Air) {
      const closing = Math.hypot(f.vel.x, f.vel.z) * T.BOT.predictSec;
      const predDist = Math.max(0, p.targetDist - closing);
      const pHit = clamp01(1 - Math.abs(predDist - FLYING_KICK_IDEAL) / FLYING_KICK_TOL) * (p.targetAir ? 0.5 : 1);
      if (pHit > T.BOT.flyingKickMinHit && Math.abs(p.targetAngle) <= halfCone) {
        this.setStick(p.dirX, p.dirZ);
        this.queueTap(Btn.Heavy, TAP_HOLD);
        this.lastAttackTick = tick;
        this.lastBranch = BRANCH_ATTACK;
        return true;
      }
      return false;
    }

    // koşma bağlamında %30 Dash Tackle (DESIGN §12 5; zar think başına)
    if (running && p.targetDist <= DASH_RANGE && p.targetDist > range && Math.abs(p.targetAngle) <= halfCone &&
        !p.targetLying && this.noise(tick, SALT_DASH) < T.BOT.dashChance * this.aggroScale) {
      this.setStick(p.dirX, p.dirZ);
      this.queueTap(Btn.Light, TAP_HOLD);
      this.comboPresses = 0; this.lastAttackTick = tick;
      this.lastBranch = BRANCH_ATTACK;
      return true;
    }

    if (p.targetDist > range || Math.abs(p.targetDy) > T.AIM.maxHeightDiff) return false;
    if (p.targetLying) return false;   // yatan: fırsat dalı (Kick/kaldır) karar verir; hafif seri yatana vurmaz
    // rakibe dönük değilsem önce dön (çubuk ona doğru), basma
    if (Math.abs(p.targetAngle) > halfCone) {
      this.setStick(p.dirX * TURN_STICK, p.dirZ * TURN_STICK);
      this.lastBranch = BRANCH_ATTACK;
      return true;
    }
    // menzilde: çubuk küçük (dönmek için; koşma bağlamı açılmasın)
    this.setStick(p.dirX * FACE_STICK, p.dirZ * FACE_STICK);
    this.lastBranch = BRANCH_ATTACK;
    // rakip havada → bekle ya da Kick (kapsül tahmini: ayak yüksekliği ≤ maxHeightDiff ve yakın)
    if (p.targetAir) {
      if (p.targetDy <= T.AIM.maxHeightDiff * 0.5 && this.noise(tick, SALT_AGGRO) < this.aggro) {
        this.queueTap(Btn.Heavy, TAP_HOLD); this.lastAttackTick = tick;
      }
      return true;
    }
    // rakip blokta → silahsızsa tutma (tutma bloğu yener). Silahlıyken B = silahı fırlat (DESIGN §2; o da bloklanır ve bot silahsız
    // kalırdı) → guard'ı eriten silah ağırını (2H Chop −45 / WH; Y basılı: ıskada holdBranch) aggression zarıyla dene, yoksa bekle.
    if (p.targetBlocking) {
      if (f.state !== FState.Attack) {
        if (f.weapon === 'unarmed') { this.queueTap(Btn.Grab, TAP_HOLD); this.lastAttackTick = tick; }
        else if (this.noise(tick, SALT_AGGRO) < this.aggro) { this.queueTap(Btn.Heavy, HEAVY_HOLD); this.lastAttackTick = tick; }
      }
      return true;
    }
    // X·X·X + comboFinish ile 4. girdi (Y / B, aggression'a göre); sim hit+2 / r+1 iptal pencerelerini tampondan okur
    if (f.state === FState.Attack && f.phase === MovePhase.Windup) return true;   // windup'ta yeni basış anlamsız
    if (this.comboPresses >= 3) {
      if (this.noise(tick, SALT_FINISH) < P.comboFinish) {
        if (this.noise(tick, SALT_FINISH_KIND) < this.aggro) this.queueTap(Btn.Heavy, HEAVY_HOLD);
        else this.queueTap(Btn.Grab, TAP_HOLD);
      }
      this.comboPresses = 0; this.lastAttackTick = tick;
      this.chainRestPending = CHAIN_REST_TICKS[this.difficulty] > 0;   // zincir bitti → hamle kapanınca tepki penceresi
      return true;
    }
    // zincir dinlenmesi (normal): yeni seri açma, yüzü dönük bekle (tehdit/fırsat dalları bu noktaya gelmeden karar verir)
    if (this.comboPresses === 0 && f.state !== FState.Attack && tick < this.chainRestUntil) return true;
    // serinin ilk basışı: aggression'a göre (hemen ya da bir sonraki think'te)
    if (this.comboPresses === 0 && f.state !== FState.Attack && this.noise(tick, SALT_AGGRO) > this.aggro + 0.5) return true;
    this.queueTap(Btn.Light, TAP_HOLD);
    this.comboPresses++; this.lastAttackTick = tick;
    return true;
  }

  // ── 6. konumlanma ──
  private thinkPosition(f: Fighter, tick: number): void {
    const p = this.perception;
    const P = this.params;
    this.lastBranch = BRANCH_POSITION;
    if (p.targetId === NO_ID) { this.setStick(0, 0); return; }
    const range = this.meleeRange(f);
    // hedef nokta: rakibi kendimle kenar arasına al (edgeAwareness zarı) → rakibin merkez tarafında dur
    let gx = p.targetX, gz = p.targetZ;
    if (this.noise(tick, SALT_EDGE + 1) < P.edgeAwareness) {
      const ex = p.targetEdgeNx, ez = p.targetEdgeNz;   // rakibin kenar dış normali: onun merkez tarafı = −n
      gx -= ex * range * 0.8; gz -= ez * range * 0.8;
    }
    let dx = gx - f.pos.x, dz = gz - f.pos.z;
    const gd = Math.hypot(dx, dz);
    if (gd > 1e-6) { dx /= gd; dz /= gd; }
    if (p.targetDist > T.BOT.strafeDist) {
      // uzak: koş (tam çubuk → koşma bağlamı; yaklaşırken strafe yok)
      this.setStick(dx, dz);
      return;
    }
    if (p.targetDist > range || this.whiffWaitUntil > tick) {
      // 2.5 u bandında rastgele strafe + yaklaşma bileşeni (aggression); whiff beklemesinde yalnız strafe
      if (tick >= this.strafeUntil) { this.strafeSide = this.noise(tick, SALT_STRAFE) < 0.5 ? 1 : -1; this.strafeUntil = tick + STRAFE_HOLD_TICKS; }
      const sx = -p.dirZ * this.strafeSide, sz = p.dirX * this.strafeSide;
      const approach = this.whiffWaitUntil > tick ? 0 : (this.noise(tick, SALT_APPROACH) < P.aggression + 0.4 ? 1 : 0.3);
      this.setStick(sx * 0.7 + dx * approach, sz * 0.7 + dz * approach);
      return;
    }
    // menzilde ama saldırı dalı basmadı (rakip havada/dönük değil vb.): yüzünü dön
    this.setStick(p.dirX * FACE_STICK, p.dirZ * FACE_STICK);
  }

  // ───────────────────────── frame ─────────────────────────
  /** Her tick: niyet → InputFrame. pressed/released yalnız değişim tick'inde; dokunuşlar TAP_HOLD tick basılı; mash sayacı. */
  frame(out: InputFrame, tick: number): void {
    // gecikmeli tehdit tepkisi
    if (this.pendingReaction !== REACT_NONE && tick >= this.reactAt) this.applyReaction(tick);
    // parry dokunuşu: impact − 3 t'de L1 basış, 1 t sonra bırakış; o an L1 basılı olamaz
    if (tick === this.parryTapAt) { this.intent.buttons &= ~Btn.Block; this.queueTap(Btn.Block, 1); this.parryTapAt = NO_TICK; }
    else if (this.parryTapAt !== NO_TICK && tick > this.parryTapAt) this.parryTapAt = NO_TICK;

    let buttons = this.intent.buttons;
    // kurtulma mash'i: mashRate basış/s, 4 yüz tuşu sırayla
    if (this.mashing) {
      this.mashAcc += this.params.mashRate / TICK_HZ;
      if (this.mashAcc >= 1) {
        this.mashAcc -= 1;
        const b = MASH_CYCLE[this.mashIdx] ?? Btn.Jump;
        this.mashIdx = (this.mashIdx + 1) & 3;
        this.queueTap(b, 1);
      }
    } else this.mashAcc = 0;

    // dokunuşlar: zaten basılı olan bit için önce bırak (bu tick), sonraki tick bas → kenar kaybolmaz
    let down = 0;
    for (let i = 0; i < BTN_BITS; i++) if ((this.holdLeft[i] ?? 0) > 0) down |= 1 << i;
    let pend = this.tapPending;
    const conflict = pend & (down | buttons);
    pend &= ~conflict;
    this.tapPending = conflict;
    if (conflict !== 0) {
      for (let i = 0; i < BTN_BITS; i++) if (conflict & (1 << i)) this.holdLeft[i] = 0;
      down &= ~conflict;
      buttons &= ~conflict;
    }
    if (pend !== 0) {
      for (let i = 0; i < BTN_BITS; i++) if (pend & (1 << i)) this.holdLeft[i] = this.tapHold[i] ?? TAP_HOLD;
      down |= pend;
    }
    buttons |= down;

    out.buttons = buttons;
    out.pressed = buttons & ~this.prevButtons;
    out.released = this.prevButtons & ~buttons;
    this.prevButtons = buttons;
    out.moveX = this.intent.moveX; out.moveZ = this.intent.moveZ;
    out.aimYaw = NaN; out.fromMouse = false;

    for (let i = 0; i < BTN_BITS; i++) if ((this.holdLeft[i] ?? 0) > 0) this.holdLeft[i] = (this.holdLeft[i] ?? 0) - 1;
  }
}

/** Bot yaratır (kurulum; WP9: oyuncu dışındaki her dövüşçü için bir tane, `world.fighters[id].isBot = true`). */
export function createBot(fighterId: number, difficulty: Difficulty, seed: number = fighterId + 1): BotBrain {
  return new BotBrain(fighterId, difficulty, seed);
}
